function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}


class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    if (this.input) {
      this.input.addEventListener('change', this.onInputChange.bind(this));
    }

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    if (this.input) {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const buttonMinus = this.querySelector(".quantity__button[name='minus']");
        buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        const buttonPlus = this.querySelector(".quantity__button[name='plus']");
        buttonPlus.classList.toggle('disabled', value >= max);
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);


class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    if (event.target.classList.contains('freeProduct-radio')) {
      return false;
    }
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0 && event.target.step != undefined) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.main-cart-page_wrap'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      }
      // {
      //   id: 'main-cart-footer',
      //   section: document.getElementById('main-cart-footer').dataset.id,
      //   selector: '.js-contents'
      // }
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);

        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
        const finalPrice = parsedState.items.find((item) => item.properties && item.properties.__free_gift);
        const totalPrice = finalPrice ? parsedState.items_subtotal_price - finalPrice.final_price : parsedState.items_subtotal_price;

        if (finalPrice && parsedState.items_subtotal_price !== 0 && totalPrice < window.requiredAmount && window.addFreeGift) {
          const indexFreeGift = parsedState.items.findIndex((item) => item.properties && item.properties.__free_gift);
          const lineItemIndex = indexFreeGift + 1
          cartItems.updateQuantity(lineItemIndex, 0);
          window.addFreeGift = false;
        }

        const productFreeItemFound = parsedState.items.find((item) => item.properties && item.properties._free_gift_product);
        if (productFreeItemFound) {
          const matchedItem = parsedState.items.find(item => item.product_id == productFreeItemFound.properties.__main_product_id);

          if (matchedItem == undefined && window.addProductFreeGift) {
            const indexFreeGiftMain = parsedState.items.findIndex((item) => item.properties && item.properties._free_gift_product);
            const lineItemMainIndex = indexFreeGiftMain + 1
            cartItems.updateQuantity(lineItemMainIndex, 0);
          }
        }

        cartUpsellSwiper();
      })
      .catch((response) => {
        console.log("response", response);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (html == null) {
      return false
    } else {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

class freeGiftModal extends HTMLElement {
  constructor() {
    super();
    this.modal = this;

    this.freeGiftedButton = this.querySelector('#free-gift-submit');
    this.freeGiftedButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (window.location.pathname == '/cart') {
        this.cart = document.querySelector('cart-items');
      } else {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      let formData = {
        'items': [{
          'id': parseInt(this.querySelector('.freeProduct-radio:checked').value),
          'quantity': 1,
          'properties': {
            '__free_gift': true
          }
        }],
        'sections': this.cart.getSectionsToRender().map((section) => section.id),
        'sections_url': window.location.pathname
      };

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
        .then(response => {
          return response.json();
        }).then(response => {
          this.modal.removeAttribute('open');
          document.querySelector('.overlay-modal').classList.remove('active');
          if (window.location.pathname == '/cart') {
            const parsedState = response;
            this.cart.getSectionsToRender().forEach((section) => {
              document.getElementById(section.id).innerHTML = this.cart.getSectionInnerHTML(
                parsedState.sections[section.id],
                section.selector
              );
            });

          } else {
            this.cart.renderContents(response);
          }

          window.addFreeGift = true;
        })
        .catch((error) => {
          console.error('Error:', error);
        });


    })

    this.freeGiftCloseBtn = this.querySelector('#free-gift-close');
    this.freeGiftCloseBtn.addEventListener('click', (event) => {
      event.preventDefault();
      this.removeAttribute('open');
      document.querySelector('.overlay-modal').classList.remove('active');
    });
  }
}

customElements.define('free-gift-modal', freeGiftModal);

class freeProductSlider extends HTMLElement {
  constructor() {
    super();
    cartUpsellSwiper();
  }
}

customElements.define('free-product-slider', freeProductSlider);

// class freeProductGoal extends HTMLElement {
//   constructor() {
//     super();
//     console.log(this.dataset.minimumAmount);
//     this.setAttribute('data-minimum-amount', window.requiredAmount);
//     this.processBar = this.querySelector('progress-bar');
    
//     if(window.cartTotal < window.requiredAmount) {
//       this.classList.remove('free-shipping-goal--done');
//       this.querySelector(`[data-left-to-spend]`).textContent = `${window.Shopify.formatMoney(window.requiredAmount - window.cartTotal, window.moneyFormat)}`;
//     } else {
//       this.classList.add('free-shipping-goal--done');
//     }

//     const percentage = Math.round((window.cartTotal / window.requiredAmount) * 100);
//     this.processBar.style.setProperty('--percent', `${percentage}%`);

    

//     console.log('percentage=========>',percentage)
//   }
// }

// customElements.define('free-product-goal', freeProductGoal);

// class freeProductGiftModal extends HTMLElement {
//   constructor() {
//     super();
//     this.modal = this;

//     this.freeGiftedButton = this.querySelector('#free-product-gift-submit');
//     this.freeGiftedButton.addEventListener('click', (event) => {
//       event.preventDefault();
//       if (window.location.pathname == '/cart') {
//         this.cart = document.querySelector('cart-items');
//       } else {
//         this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
//       }
//       let formData = {
//         'items': [{
//           'id': parseInt(this.querySelector('.freeProduct-radio:checked').value),
//           'quantity': 1,
//           'properties': {
//             '_free_gift_product': true,
//             '__main_product_id': this.dataset.mainProductId
//           }
//         }],
//         'sections': this.cart.getSectionsToRender().map((section) => section.id),
//         'sections_url': window.location.pathname
//       };

//       fetch(window.Shopify.routes.root + 'cart/add.js', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json'
//         },
//         body: JSON.stringify(formData)
//       })
//         .then(response => {
//           return response.json();
//         }).then(response => {

//           this.modal.removeAttribute('open');
//           document.querySelector('.overlay-modal').classList.remove('active');

//           if (window.location.pathname == '/cart') {
//             const parsedState = response;
//             this.cart.getSectionsToRender().forEach((section) => {
//               document.getElementById(section.id).innerHTML = this.cart.getSectionInnerHTML(
//                 parsedState.sections[section.id],
//                 section.selector
//               );
//             });

//           } else {
//             this.cart.renderContents(response);
//           }


//           window.addProductFreeGift = true;
//         })
//         .catch((error) => {
//           console.error('Error:', error);
//         });
//     })
//   }
// }

// customElements.define('free-product-gift-modal', freeProductGiftModal);

class freeProductGiftModal extends HTMLElement {
  constructor() {
    super();
    this.modal = this;

    this.freeGiftedButton = this.querySelector('#free-product-gift-submit');
    this.freeGiftedButton.addEventListener('click', (event) => {
      event.preventDefault();

      // Determine which cart DOM element to update
      if (window.location.pathname === '/cart') {
        this.cart = document.querySelector('cart-items');
      } else {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      // Get selected gift radio button
      const selectedRadio = this.querySelector('.freeProduct-radio:checked');
      if (!selectedRadio) {
        alert('Please select a free gift.');
        return;
      }

      // Find the selected gift's container
      const giftCard = selectedRadio.closest('.freeProduct-grid');

      // Find the associated variant dropdown inside that product
      const variantSelect = giftCard.querySelector('select.variant-dropdown');
      // Get the data-variant-id from the product container
      const dataVariantId = giftCard.querySelector('.freeProduct-info').getAttribute('data-variant-id');

      // Check if the variant id is available
      const selectedVariantId = variantSelect ? parseInt(variantSelect.value) : parseInt(dataVariantId);
      // const selectedVariantId = variantSelect ? parseInt(variantSelect.value) : null;

      if (!selectedVariantId) {
        alert('Please select a free gift product.');
        return;
      }

      // Prepare form data for Shopify AJAX cart add
      let formData = {
        'items': [{
          'id': selectedVariantId,
          'quantity': 1,
          'properties': {
            '_free_gift_product': true,
            '__main_product_id': this.dataset.mainProductId
          }
        }],
        'sections': this.cart.getSectionsToRender().map((section) => section.id),
        'sections_url': window.location.pathname
      };

      // Send AJAX request to add gift variant
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      .then(response => response.json())
      .then(response => {
        // Close modal and remove overlay
        this.modal.removeAttribute('open');
        document.querySelector('.overlay-modal').classList.remove('active');

        // Update cart contents based on location
        if (window.location.pathname === '/cart') {
          const parsedState = response;
          this.cart.getSectionsToRender().forEach((section) => {
            document.getElementById(section.id).innerHTML = this.cart.getSectionInnerHTML(
              parsedState.sections[section.id],
              section.selector
            );
          });
        } else {
          this.cart.renderContents(response);
        }

        window.addProductFreeGift = true;
      })
      .catch((error) => {
        console.error('Error adding free gift:', error);
      });
    });
  }
}

customElements.define('free-product-gift-modal', freeProductGiftModal);


class CartRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.innerHTML = `<div class="loading-overlay loading-overlay--visible"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg></div>`
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.loadRecommendations(productId);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}&intent=complementary`)
      .then((response) => response.text())
      .then((text) => {
        if(text) {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('cart-recommendations');
  
          if (recommendations?.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }
  
          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }
  
          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
        } else {
          document.querySelector('cart-complementary-products').innerHTML = '';
        }
      })
      .catch((e) => {
        console.error('===========Error=======>',e);
      }).finally(() => {
        
      });
  }
}

customElements.define('cart-complementary-products', CartRecommendations);